import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAggregatedPicks } from '../hooks/useAggregatedPicks';
import { useRecommenders } from '../hooks/useRecommenders';
import { useSession } from '../hooks/useSession';
import { mapsUrlForPick } from '../lib/recommenderShape';
import { rankedAggregatedPicks } from '../lib/aggregateRanking';
import { isFullList } from '../lib/fullList';
import Header from './Header';
import Footer from './Footer';
import Skeleton from './Skeleton';
import ContributeCTA from './ContributeCTA';

interface AggregatedRow {
  placeId: string;
  name: string;
  cuisine: string;
  location: string;
  googleMapsUrl: string;
  timesRecommended: number;
  averageRank: number;
  url: string;
}

const AllRestaurantsPage: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useSession();
  const { picks: aggregatedPicks, isLoaded: aggLoaded, error: aggError } = useAggregatedPicks();
  const { recommenders, isLoaded: recLoaded, error: recError } = useRecommenders();

  // Rank first (shared with the per-restaurant "overall rank" badge), then map
  // to the display shape so the row order matches the rest of the site.
  const rows = useMemo<AggregatedRow[]>(
    () =>
      rankedAggregatedPicks(aggregatedPicks).map((p) => ({
        placeId: p.placeId,
        name: p.sampleName,
        cuisine: p.sampleCuisine ?? 'Restaurant',
        // Google addresses end in ", USA" — noise on an all-STL site (the
        // maps URL below keeps the full string).
        location: (p.sampleLocation ?? '').replace(/, (USA|United States)$/, ''),
        googleMapsUrl: mapsUrlForPick({
          name: p.sampleName,
          location: p.sampleLocation ?? '',
          placeId: p.placeId,
        }),
        timesRecommended: p.userPickCount,
        averageRank: p.userAvgRank,
        url: `/place/${p.placeId}`,
      })),
    [aggregatedPicks]
  );

  // Count only published (full) lists so this number always matches the
  // recommenders grid and the gated aggregated_picks view.
  const recommenderCount = recommenders.filter((r) =>
    isFullList(r.restaurants.length)
  ).length;

  const getRankColor = (averageRank: number) => {
    if (averageRank <= 1.5) return 'text-amber';
    if (averageRank <= 2.5) return 'text-terracotta';
    return 'text-charcoal-light';
  };

  const error = aggError ?? recError;
  const isLoaded = aggLoaded && recLoaded;

  return (
    <div className="min-h-screen">
      <Header />
      {/* Hero Section */}
      <div className="pt-20 pb-12 md:pt-32 md:pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-charcoal mb-4 tracking-tight">
              Rankum
            </h1>
            <p className="text-lg md:text-xl text-charcoal-light font-light leading-relaxed mb-8 max-w-[38rem] mx-auto">
              Forget the critic reviews and the Google Maps rabbit hole. People
              in St. Louis rank their five favorite restaurants, and we add up
              every list into the ranking below.
            </p>

            {/* Stats Badges — show skeletons until data lands so the
                badges don't flash "0" then jump to the real count. */}
            <div className="flex items-center justify-center gap-3 mb-10">
              <div className="stat-badge">
                {isLoaded ? (
                  <span className="text-amber font-semibold">{rows.length}</span>
                ) : (
                  <Skeleton className="h-4 w-5" />
                )}
                <span className="text-charcoal-light">restaurants</span>
              </div>
              <div className="stat-badge">
                {isLoaded ? (
                  <span className="text-amber font-semibold">{recommenderCount}</span>
                ) : (
                  <Skeleton className="h-4 w-5" />
                )}
                <span className="text-charcoal-light">recommenders</span>
              </div>
            </div>

            {/* Lead with the contribution action — growing the recommender base
                is the flywheel — while keeping a browse path for newcomers. */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {session ? (
                <Link to="/my-list" className="btn-primary">
                  <span>Edit your rankings</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              ) : (
                <Link to="/sign-up" className="btn-primary">
                  <span>Rank your top five</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              )}
              <Link to="/recommenders" className="btn-secondary">
                Meet Our Recommenders
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="divider mb-12"></div>

      {/* Restaurant List Section */}
      <div className="container mx-auto px-6 pb-16">
        {error ? (
          <p className="text-charcoal-light text-center" role="alert">
            Couldn&rsquo;t load restaurants: {error}
          </p>
        ) : !isLoaded ? (
          <div aria-busy="true" aria-label="Loading restaurants">
            {/* Mobile card skeletons */}
            <div className="md:hidden space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="card p-5">
                  <Skeleton className="h-7 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-1/4 mb-4" />
                  <Skeleton className="h-4 w-2/3 mb-4" />
                  <div className="flex gap-4 pt-3 border-t border-black/5">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table skeleton */}
            <div className="hidden md:block card overflow-hidden">
              <div className="px-6 py-5 border-b border-black/5">
                <Skeleton className="h-4 w-24" />
              </div>
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="px-6 py-5 flex items-center gap-6 border-b border-black/5 last:border-b-0"
                >
                  <Skeleton className="h-6 w-8" />
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-6 w-8 ml-auto" />
                  <Skeleton className="h-6 w-10" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ))}
            </div>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-charcoal-light text-center">No restaurants yet. Sign up and add yours.</p>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {rows.map((restaurant, index) => (
                <Link
                  key={restaurant.placeId}
                  to={restaurant.url}
                  className="card p-5 block hover:shadow-lg hover:border-amber/30 transition-all duration-300"
                >
                  {/* Rank + name - full width, prominent */}
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="font-display text-2xl text-charcoal/30 flex-shrink-0">
                      #{index + 1}
                    </span>
                    <h3 className="font-display text-2xl text-amber leading-tight flex items-center gap-2">
                      {restaurant.name}
                      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </h3>
                  </div>

                  {/* Cuisine */}
                  <p className="text-amber font-medium text-sm mb-4">{restaurant.cuisine}</p>

                  {/* Location with directions */}
                  {restaurant.location && (
                    <a
                      href={restaurant.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-start gap-2 text-sm text-charcoal-light hover:text-amber transition-colors duration-300 mb-4"
                    >
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="link-underline">{restaurant.location}</span>
                    </a>
                  )}

                  {/* Stats bar - subtle metadata */}
                  <div className="flex items-center gap-4 pt-3 border-t border-black/5 text-xs text-charcoal-light">
                    <span>
                      Picked <span className="text-amber font-semibold">{restaurant.timesRecommended}×</span>
                    </span>
                    <span>
                      Avg placement{' '}
                      <span className={`font-semibold ${getRankColor(restaurant.averageRank)}`}>
                        {restaurant.averageRank.toFixed(1)}
                      </span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-black/5">
                      <th className="pl-6 pr-2 py-5 text-left text-xs font-medium text-charcoal-light uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-5 text-left text-xs font-medium text-charcoal-light uppercase tracking-wider">Restaurant</th>
                      <th className="px-4 py-5 text-center text-xs font-medium text-charcoal-light uppercase tracking-wider">Picks</th>
                      <th className="px-4 py-5 text-center text-xs font-medium text-charcoal-light uppercase tracking-wider">Avg. Placement</th>
                      <th className="px-4 py-5 text-left text-xs font-medium text-charcoal-light uppercase tracking-wider">Cuisine</th>
                      {/* Addresses only fit comfortably from lg up; md keeps five columns. */}
                      <th className="hidden lg:table-cell px-4 py-5 text-left text-xs font-medium text-charcoal-light uppercase tracking-wider">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-black/5">
                    {rows.map((restaurant, index) => (
                      <tr
                        key={restaurant.placeId}
                        onClick={() => navigate(restaurant.url)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(restaurant.url);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`View details for ${restaurant.name}`}
                        className="group hover:bg-cream-dark/50 transition-colors duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber focus:ring-inset"
                      >
                        <td className="pl-6 pr-2 py-5">
                          <span className="font-display text-lg text-charcoal/30">#{index + 1}</span>
                        </td>
                        <td className="px-4 py-5">
                          <span className="font-display text-lg text-amber transition-colors duration-300 inline-flex items-center gap-2">
                            {restaurant.name}
                            <svg className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </span>
                        </td>
                        <td className="px-4 py-5 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber/10 text-amber text-sm font-semibold">
                            {restaurant.timesRecommended}
                          </span>
                        </td>
                        <td className="px-4 py-5 text-center">
                          <span className={`font-display text-lg ${getRankColor(restaurant.averageRank)}`}>
                            {restaurant.averageRank.toFixed(1)}
                          </span>
                        </td>
                        <td className="px-4 py-5 text-charcoal-light text-sm">{restaurant.cuisine}</td>
                        <td className="hidden lg:table-cell px-4 py-5">
                          {restaurant.location ? (
                            <a
                              href={restaurant.googleMapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm text-charcoal-light hover:text-amber transition-colors duration-300 link-underline"
                            >
                              {restaurant.location}
                            </a>
                          ) : (
                            <span className="text-sm text-charcoal-light">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Catch people who've scrolled the whole ranking with the same
          contribution CTA used on every restaurant page. */}
      <ContributeCTA secondaryLink={{ to: '/recommenders', label: 'Meet our recommenders' }} />

      <Footer />
    </div>
  );
};

export default AllRestaurantsPage;
